import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import parentService from '../../services/parentService';
import { BookOpen, Money } from '@phosphor-icons/react';

function ParentLibrarySection({ childId, childName }) {
  const [loans, setLoans] = useState([]);
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [loansData, finesData] = await Promise.all([
          parentService.getChildLibraryLoans(childId),
          parentService.getChildLibraryFines(childId),
        ]);
        if (!cancelled) {
          setLoans(loansData || []);
          setFines((finesData.items || []).filter((f) => f.status === 'pending'));
        }
      } catch (error) {
        if (!cancelled) toast.error(error.message || "Unable to load your child's library activity");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [childId]);

  if (loading) return <div className="empty-state">Loading library activity...</div>;

  const activeLoans = loans.filter((l) => l.status === 'active');
  const pastLoans = loans.filter((l) => l.status !== 'active');

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card" style={{ padding: 16, display: 'grid', gap: 12 }}>
        <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
          <BookOpen size={16} /> {childName ? `${childName}'s` : "Your child's"} Borrowed Books
        </div>
        {activeLoans.length === 0 ? (
          <div className="empty-state">No books currently borrowed.</div>
        ) : (
          <div style={{ display: 'grid', gap: 6 }}>
            {activeLoans.map((loan) => (
              <div key={loan.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderBottom: '1px solid #f3f4f6', paddingBottom: 4 }}>
                <span>{loan.item_title}</span>
                <span style={{ color: loan.is_overdue ? '#dc2626' : '#6b7280' }}>Due {loan.due_date}{loan.is_overdue ? ' (Overdue)' : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {fines.length > 0 && (
        <div className="card" style={{ padding: 16, display: 'grid', gap: 12 }}>
          <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Money size={16} /> Outstanding Library Fines
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {fines.map((fine) => (
              <div key={fine.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderBottom: '1px solid #f3f4f6', paddingBottom: 4 }}>
                <span>{fine.item_title} — {fine.reason}</span>
                <span style={{ color: '#dc2626', fontWeight: 600 }}>GHS {fine.amount.toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Fines are payable through the school's regular fee payment process.</div>
        </div>
      )}

      {pastLoans.length > 0 && (
        <div className="card" style={{ padding: 16, display: 'grid', gap: 8 }}>
          <div style={{ fontWeight: 700 }}>Past Loans</div>
          <div style={{ display: 'grid', gap: 6, maxHeight: 260, overflow: 'auto' }}>
            {pastLoans.map((loan) => (
              <div key={loan.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderBottom: '1px solid #f3f4f6', paddingBottom: 4 }}>
                <span>{loan.item_title}</span>
                <span className="badge">{loan.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParentLibrarySection;
